"use client";

import { useState, useEffect } from "react";
import type { FormEvent } from "react";
import { useAction } from "convex/react";
import { useLocale, useTranslations } from "next-intl";
import { ChevronLeft } from "lucide-react";
import { Link } from "@/i18n/navigation";
import posthog from "posthog-js";
import { api } from "../../../convex/_generated/api";
import { AuthCard } from "./auth-card";
import { AuthInput } from "./auth-input";
import { AuthButton } from "./auth-button";
import { errorCode } from "./error-mapping";
import { cn } from "@/lib/utils";

type Provider = "google" | "apple";

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M21.35 11.1H12v3.2h5.35c-.24 1.4-1.63 4.1-5.35 4.1-3.22 0-5.85-2.66-5.85-5.95s2.63-5.95 5.85-5.95c1.83 0 3.05.78 3.75 1.45l2.56-2.46C16.73 4.02 14.63 3 12 3 6.98 3 3 6.98 3 12s3.98 9 9 9c5.2 0 8.64-3.66 8.64-8.81 0-.59-.07-1.04-.29-2.09Z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="16" height="18" viewBox="0 0 16 18" aria-hidden="true" fill="currentColor">
      <path d="M13.11 9.58c-.02-2.33 1.9-3.46 1.99-3.52-1.09-1.59-2.77-1.8-3.37-1.83-1.44-.15-2.8.84-3.53.84-.73 0-1.85-.82-3.05-.8-1.57.02-3.01.91-3.81 2.31-1.63 2.82-.42 7 1.17 9.29.78 1.12 1.71 2.38 2.93 2.33 1.18-.05 1.63-.76 3.05-.76 1.42 0 1.82.76 3.06.73 1.27-.02 2.07-1.14 2.84-2.27.9-1.3 1.27-2.57 1.29-2.64-.03-.01-2.46-.94-2.48-3.72M10.7 2.6c.65-.78 1.08-1.87.96-2.95-.93.04-2.06.62-2.72 1.4-.6.7-1.12 1.81-.98 2.87 1.04.08 2.1-.53 2.75-1.32" />
    </svg>
  );
}

export function ForgotPasswordForm() {
  const t = useTranslations("auth");
  const locale = useLocale();
  const requestPasswordReset = useAction(api.emailAuth.requestPasswordReset);

  const [email, setEmail] = useState("");
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [oauthProvider, setOauthProvider] = useState<Provider | null>(null);
  const [sent, setSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErrorKey(null);
    try {
      const res = await requestPasswordReset({ email: email.trim().toLowerCase(), locale });
      if (res.status === "oauth_only") {
        setOauthProvider(res.provider);
        posthog.capture("password_reset_oauth_only", { provider: res.provider });
      } else {
        setSent(true);
        setResendCooldown(30);
        posthog.capture("password_reset_requested");
      }
    } catch (err) {
      const code = errorCode(err);
      setErrorKey(code);
      posthog.capture("password_reset_request_failed", { error_code: code });
    } finally {
      setLoading(false);
    }
  }

  function goToProvider(provider: Provider) {
    setOauthLoading(true);
    posthog.capture("sign_in_clicked", { source: "forgot_password_oauth_only", provider });
    const next = encodeURIComponent(`/${locale}/new`);
    window.location.href = `/api/auth/oauth/start?provider=${provider}&locale=${locale}&next=${next}`;
  }

  if (oauthProvider) {
    const titleKey = oauthProvider === "apple" ? "forgot.oauth.titleApple" : "forgot.oauth.titleGoogle";
    const bodyKey = oauthProvider === "apple" ? "forgot.oauth.bodyApple" : "forgot.oauth.bodyGoogle";
    const ctaKey =
      oauthProvider === "apple" ? "forgot.oauth.continueApple" : "forgot.oauth.continueGoogle";

    return (
      <AuthCard title={t(titleKey)} subtitle={t(bodyKey)}>
        <button
          type="button"
          onClick={() => goToProvider(oauthProvider)}
          disabled={oauthLoading}
          className={cn(
            "inline-flex h-12 w-full items-center justify-center gap-2 rounded-full border border-line bg-surface px-4 text-[15px] font-medium text-ink transition-colors",
            "hover:bg-surface-mute hover:border-line-strong",
            oauthLoading && "opacity-70 cursor-wait",
          )}
        >
          {oauthLoading ? (
            <span className="inline-block h-4 w-4 rounded-full border-2 border-ink/30 border-t-ink animate-spin" />
          ) : oauthProvider === "apple" ? (
            <AppleIcon />
          ) : (
            <GoogleIcon />
          )}
          <span className="leading-none">{t(ctaKey)}</span>
        </button>
        <Link
          href="/sign-in"
          className="inline-flex h-12 w-full items-center justify-center rounded-full text-[14px] font-medium text-ink-mute hover:text-ink transition-colors"
        >
          {t("forgot.backToSignIn")}
        </Link>
      </AuthCard>
    );
  }

  if (sent) {
    return (
      <AuthCard
        title={t("forgot.successTitle")}
        subtitle={
          <>
            {t("forgot.successBodyBefore")}{" "}
            <span className="font-medium text-ink">{email.trim().toLowerCase()}</span>
            {t("forgot.successBodyAfter")}
          </>
        }
      >
        <p className="rounded-2xl border border-line bg-surface-mute px-4 py-3 text-center text-[13px] leading-relaxed text-ink-dim">
          {t("forgot.successHint")}
        </p>
        <button
          onClick={async () => {
            setResendLoading(true);
            try {
              const res = await requestPasswordReset({ email: email.trim().toLowerCase(), locale });
              if (res.status === "oauth_only") {
                setOauthProvider(res.provider);
                setSent(false);
              } else {
                setResendCooldown(30);
              }
            } finally {
              setResendLoading(false);
            }
          }}
          disabled={resendCooldown > 0 || resendLoading}
          className="inline-flex h-12 w-full items-center justify-center rounded-full border border-line bg-surface text-[15px] font-medium text-ink transition-colors hover:bg-surface-mute hover:border-line-strong disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {resendLoading
            ? t("forgot.resendLoading")
            : resendCooldown > 0
              ? `${t("forgot.resendCooldown")} (${resendCooldown})`
              : t("forgot.resend")}
        </button>
        <Link
          href="/sign-in"
          className="inline-flex h-12 w-full items-center justify-center rounded-full border border-line bg-surface text-[15px] font-medium text-ink transition-colors hover:bg-surface-mute hover:border-line-strong"
        >
          {t("forgot.backToSignIn")}
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t("forgot.title")} subtitle={t("forgot.subtitle")}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <AuthInput
          label={t("forgot.emailLabel")}
          name="email"
          type="email"
          autoComplete="email"
          required
          placeholder={t("forgot.emailPlaceholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {errorKey && <p className="text-sm text-red-400 text-center">{t(errorKey)}</p>}
        <AuthButton type="submit" loading={loading} className="mt-2">
          {t("forgot.submit")}
        </AuthButton>
      </form>

      <Link
        href="/sign-in"
        className="mt-2 inline-flex items-center justify-center gap-1.5 text-[13px] text-ink-mute hover:text-ink transition-colors"
      >
        <ChevronLeft size={14} />
        {t("forgot.backToSignIn")}
      </Link>
    </AuthCard>
  );
}
